import { ArrowUturnLeftIcon, BanknotesIcon, ClipboardDocumentListIcon, EyeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FilterChip } from '../../components/ui/Chip';
import { Column, DataTable } from '../../components/ui/DataTable';
import { Field } from '../../components/ui/Field';
import { Input } from '../../components/ui/Input';
import { Money } from '../../components/ui/Money';
import { RowAction } from '../../components/ui/RowActions';
import { PaymentChip, StatusChip } from '../../components/ui/StatusChip';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import api from '../../services/api';
import { formatApprox } from '../order-detail/format';
import type { PanelOrder } from '../order-detail/types';
import type { Order, OrderStatus, PaymentStatus } from '../../types/Order';
import { HISTORY_PAGE_SIZE } from './config';
import { OrderPlace } from './OrderTicket';
import { useOrderActions } from './useOrderActions';
import { useOrderEditor } from './useOrderEditor';

dayjs.extend(relativeTime);

const STATUSES: readonly OrderStatus[] = ['pending', 'accepted', 'preparing', 'ready', 'completed', 'cancelled'];
const PAYMENTS: readonly PaymentStatus[] = ['paid', 'unpaid'];
const DAY = 'YYYY-MM-DD';

const csv = (value: string | null): string[] => (value ? value.split(',').filter(Boolean) : []);

/** Closed orders and everything else, by date. Filters live in the URL so a view can be shared and survives reload. */
export const OrderHistory = () => {
    const { t } = useTranslation(['orders', 'common', 'orderDetail']);
    const [params, setParams] = useSearchParams();
    const { locationId: activeLocation } = useActiveLocation();
    const today = dayjs().format(DAY);
    const from = params.get('from') ?? today;
    const to = params.get('to') ?? today;
    const statusParam = params.get('status');
    const payParam = params.get('pay');
    const statuses = useMemo(() => csv(statusParam), [statusParam]);
    const payments = useMemo(() => csv(payParam), [payParam]);
    const query = (params.get('q') ?? '').replace(/^#/, '').trim();
    const locationId = params.get('locationId') ?? activeLocation;

    const [rows, setRows] = useState<PanelOrder[]>([]);
    const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setState('loading');
        api.get<PanelOrder[]>('/orders', {
            params: {
                status: 'all',
                locationId: locationId ?? undefined,
                from: dayjs(from).startOf('day').toISOString(),
                to: dayjs(to).endOf('day').toISOString(),
            },
        })
            .then(({ data }) => {
                if (cancelled) return;
                setRows([...data].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)));
                setState('ready');
            })
            .catch(() => {
                if (!cancelled) setState('error');
            });
        return () => {
            cancelled = true;
        };
    }, [from, to, locationId, attempt]);

    const patch = useCallback(
        (id: string, change: Partial<Order>) => setRows((list) => list.map((o) => (o.id === id ? { ...o, ...change } : o))),
        []
    );
    const actions = useOrderActions(useOrderEditor(rows, patch));

    const setFilter = (key: string, value: string | null) =>
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value) next.set(key, value);
                else next.delete(key);
                next.delete('page');
                return next;
            },
            { replace: true }
        );
    const toggle = (key: string, current: string[], value: string) =>
        setFilter(key, (current.includes(value) ? current.filter((v) => v !== value) : [...current, value]).join(','));

    const visible = useMemo(
        () =>
            rows.filter(
                (o) =>
                    (statuses.length === 0 || statuses.includes(o.status)) &&
                    (payments.length === 0 || payments.includes(o.paymentStatus)) &&
                    (query === '' || o.orderNumber.toLowerCase().includes(query.toLowerCase()))
            ),
        [rows, statuses, payments, query]
    );

    const closedOf = (o: PanelOrder) => o.status === 'completed' || o.status === 'cancelled';
    const none = (
        <>
            <span aria-hidden="true" className="text-muted">
                —
            </span>
            <span className="sr-only">{t('orderDetail:history.none')}</span>
        </>
    );

    const columns: Column<PanelOrder>[] = [
        { id: 'order', header: t('history.order'), priority: 'primary', cell: (o) => `#${o.orderNumber}` },
        {
            id: 'table',
            header: t('history.table'),
            priority: 'secondary',
            cell: (o) => <OrderPlace order={o} />,
        },
        ...(locationId === null
            ? [
                  {
                      id: 'location',
                      header: t('detail.location'),
                      priority: 'secondary' as const,
                      cell: (o: PanelOrder) => <span className="block max-w-[16ch] truncate" title={o.locationName}>{o.locationName}</span>,
                  },
              ]
            : []),
        {
            id: 'time',
            header: t('history.time'),
            priority: 'secondary',
            cell: (o) => (
                <time dateTime={o.createdAt} title={dayjs(o.createdAt).fromNow()} className="whitespace-nowrap">
                    {dayjs(o.createdAt).format('D MMM, HH:mm')}
                </time>
            ),
        },
        {
            id: 'closed',
            header: t('orderDetail:history.closed'),
            priority: 'secondary',
            cell: (o) =>
                closedOf(o) ? (
                    <time dateTime={o.timing.statusSince} className="whitespace-nowrap">
                        <span className="text-muted 2xl:hidden">{t('orderDetail:history.closed')} </span>
                        {dayjs(o.timing.statusSince).format(dayjs(o.timing.statusSince).isSame(o.createdAt, 'day') ? 'HH:mm' : 'D MMM, HH:mm')}
                    </time>
                ) : (
                    none
                ),
        },
        {
            id: 'duration',
            header: t('orderDetail:history.duration'),
            priority: 'secondary',
            align: 'end',
            cell: (o) =>
                o.status === 'completed' && o.timing.toComplete !== null ? (
                    <span className="tabular-nums">
                        <span className="text-muted 2xl:hidden">{t('orderDetail:history.duration')} </span>
                        {formatApprox(o.timing.toComplete, o.timing.estimated, t)}
                    </span>
                ) : (
                    none
                ),
        },
        {
            id: 'items',
            header: t('history.items'),
            priority: 'secondary',
            cell: (o) => (
                <span className="block max-w-[20ch] truncate">
                    {t('history.itemCount', { count: o.orderItems.reduce((n, i) => n + i.quantity, 0) })}
                    {o.orderItems.length > 0 && ` · ${o.orderItems.map((i) => i.name).join(', ')}`}
                </span>
            ),
        },
        {
            id: 'total',
            header: t('history.total'),
            priority: 'meta',
            align: 'end',
            cell: (o) => <Money amount={o.totalAmount} symbol={o.currencySymbol} position={o.currencySymbolPosition} />,
        },
        {
            id: 'payment',
            header: t('history.payment'),
            priority: 'meta',
            cell: (o) => <PaymentChip status={o.paymentStatus} method={o.paymentMethod} orderStatus={o.status} />,
        },
        { id: 'status', header: t('history.status'), priority: 'primary', cell: (o) => <StatusChip status={o.status} /> },
    ];

    const rowActions = (o: PanelOrder): RowAction[] => [
        { id: 'view', label: t('menu.details'), icon: EyeIcon, to: `/orders/${o.id}` },
        o.paymentStatus === 'unpaid'
            ? { id: 'paid', label: t('menu.paidCash'), icon: BanknotesIcon, onSelect: () => void actions.pay(o, { status: 'paid', method: 'cash' }) }
            : { id: 'unpaid', label: t('menu.unpaid'), icon: BanknotesIcon, onSelect: () => void actions.pay(o, { status: 'unpaid' }) },
        ...(o.paymentStatus === 'unpaid'
            ? [{ id: 'paid-card', label: t('menu.paidCard'), icon: BanknotesIcon, onSelect: () => void actions.pay(o, { status: 'paid', method: 'card' }) }]
            : []),
        ...(closedOf(o)
            ? [{ id: 'reopen', label: t('history.reopen'), icon: ArrowUturnLeftIcon, onSelect: () => void actions.back(o) }]
            : []),
    ];

    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(2,minmax(0,12rem))_minmax(0,16rem)]">
                <Field label={t('history.from')}>
                    {(c) => <Input {...c} type="date" value={from} max={to} onChange={(e) => e.target.value && setFilter('from', e.target.value)} />}
                </Field>
                <Field label={t('history.to')}>
                    {(c) => <Input {...c} type="date" value={to} min={from} onChange={(e) => e.target.value && setFilter('to', e.target.value)} />}
                </Field>
                <div className="sm:col-span-2 lg:col-span-1">
                    <Field label={t('history.search')}>
                        {(c) => (
                            <Input
                                {...c}
                                type="search"
                                inputMode="numeric"
                                leading={<MagnifyingGlassIcon className="size-5" aria-hidden="true" />}
                                placeholder={t('history.searchPlaceholder')}
                                value={params.get('q') ?? ''}
                                onChange={(e) => setFilter('q', e.target.value)}
                            />
                        )}
                    </Field>
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <div role="group" aria-label={t('history.statusFilter')} className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                        <FilterChip key={s} pressed={statuses.includes(s)} onToggle={() => toggle('status', statuses, s)}>
                            {t(`common:status.${s}`)}
                        </FilterChip>
                    ))}
                </div>
                <div role="group" aria-label={t('history.paymentFilter')} className="flex flex-wrap gap-2">
                    {PAYMENTS.map((p) => (
                        <FilterChip key={p} pressed={payments.includes(p)} onToggle={() => toggle('pay', payments, p)}>
                            {t(`common:status.${p}`)}
                        </FilterChip>
                    ))}
                </div>
            </div>

            <DataTable
                rows={visible}
                columns={columns}
                getRowId={(o) => o.id}
                rowHref={(o) => `/orders/${o.id}`}
                rowActions={rowActions}
                rowLabel={(o) => `#${o.orderNumber}`}
                state={state}
                onRetry={() => setAttempt((n) => n + 1)}
                pageSize={HISTORY_PAGE_SIZE}
                // The wide table (closed time and duration included) needs a large desktop screen; smaller ones get the cards, which show status and actions.
                tableFrom="2xl"
                caption={t('history.caption')}
                errorTitle={t('history.loadFailed')}
                empty={{ icon: ClipboardDocumentListIcon, title: t('history.emptyTitle'), body: t('history.emptyBody') }}
            />
        </div>
    );
};
